import React, { Component } from 'react';
import classNames from 'classnames';

import css from './Handle.module.css';

/**
 * A component that renders a handle for a range slider.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {number} props.min - The minimum value of the range slider
 * @param {number} props.max - The maximum value of the range slider
 * @param {Function} props.valueToPosition - The function to convert the value to the position
 * @param {Function} props.positionToValue - The function to convert the position to the value
 * @param {Function} props.onChange - The function to call when the value changes
 * @param {Function} props.changeActive - The function to call when the active state changes
 * @returns {JSX.Element}
 */
class Handle extends Component {
  constructor(props) {
    super(props);

    this.state = { dragging: false, relativePos: null };

    this.handleRef = React.createRef();
    this._isMounted = false;

    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    this.onTouchStart = this.onTouchStart.bind(this);
    this.onTouchMove = this.onTouchMove.bind(this);
    this.onTouchEnd = this.onTouchEnd.bind(this);

    this.onStart = this.onStart.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onEnd = this.onEnd.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.onMouseMoveListener = window.addEventListener('mousemove', this.onMouseMove, false);
    this.onMouseUpListener = window.addEventListener('mouseup', this.onMouseUp, false);
  }

  componentWillUnmount() {
    this._isMounted = false;
    window.removeEventListener('mousemove', this.onMouseMoveListener, false);
    window.removeEventListener('mouseup', this.onMouseUpListener, false);
  }

  onMouseDown(e) {
    e.stopPropagation();
    e.preventDefault();

    this.onStart(e.pageX);
  }
  onMouseMove(e) {
    if (!this.state.dragging) return;

    this.onMove(e.pageX);
  }
  onMouseUp(e) {
    e.stopPropagation();
    e.preventDefault();

    this.onEnd();
  }

  onTouchStart(e) {
    e.stopPropagation();
    e.preventDefault();

    const touchpageX = e.touches[0].pageX;
    this.onStart(touchpageX);
  }
  onTouchMove(e) {
    if (!this.state.dragging) return;

    this.onMove(e.touches[0].pageX);
  }
  onTouchEnd(e) {
    e.stopPropagation();
    e.preventDefault();

    this.onEnd();
  }

  onStart(pagePosition) {
    const { offsetLeft, offsetWidth } = this.handleRef.current;
    this.setState({ dragging: true, relativePos: pagePosition - offsetLeft - offsetWidth / 2 });
    this.props.changeActive();
  }
  onMove(pagePosition) {
    const { min, max, positionToValue } = this.props;
    const position = pagePosition - this.state.relativePos;
    const currentValue = positionToValue(position);

    const value = currentValue < min ? min : currentValue > max ? max : currentValue;

    this.props.onChange(value);
  }
  onEnd() {
    // Ensuring that setState doesn't get called.
    // This a strange behaviour since window.removeEventListener is called in componentWillUnmount
    if (this._isMounted) {
      this.setState({ dragging: false });
    }
  }

  onKeyDown = event => {
    const { value, min, max, step = 1, onChange, changeActive } = this.props;
    const targets = {
      ArrowLeft: value - step,
      ArrowDown: value - step,
      ArrowRight: value + step,
      ArrowUp: value + step,
      PageDown: value - step * 10,
      PageUp: value + step * 10,
      Home: min,
      End: max,
    };
    if (!Object.prototype.hasOwnProperty.call(targets, event.key)) return;
    event.preventDefault();
    const next = Math.min(max, Math.max(min, targets[event.key]));
    changeActive();
    if (next !== value) onChange(next);
  };

  render() {
    const { rootClassName, className, value, valueToPosition, ariaLabel, touchBuffer } = this.props;
    const position = valueToPosition(value);
    const classes = classNames(rootClassName || css.rootTouchBuffer, className);
    const ariaLabelMaybe = ariaLabel ? { ['aria-label']: ariaLabel } : {};

    // FASE2-WIZARD-2b-ter: buffer táctil recortado cuando las posiciones están
    // más juntas que él (ver RangeSlider). Sin recorte se queda el de la hoja
    // de estilos. El círculo visible mide 20px y se recentra dentro del buffer
    // nuevo; si este es menor, el círculo sobresale, que es lo correcto: lo que
    // se reduce es la zona de captura, no lo que se ve.
    const bufferStyle = touchBuffer
      ? {
          width: `${touchBuffer}px`,
          height: `${touchBuffer}px`,
          marginTop: `-${touchBuffer / 2}px`,
          marginLeft: `-${touchBuffer / 2}px`,
        }
      : {};
    const visibleOffset = touchBuffer
      ? { top: `${(touchBuffer - 20) / 2}px`, marginLeft: `${(touchBuffer - 20) / 2}px` }
      : {};

    return (
      <div
        className={classes}
        ref={this.handleRef}
        style={{ left: `${position}px`, ...bufferStyle }}
        onMouseDown={this.onMouseDown}
        onMouseMove={this.onMouseMove}
        onMouseUp={this.onMouseUp}
        onTouchStart={this.onTouchStart}
        onTouchMove={this.onTouchMove}
        onTouchEnd={this.onTouchEnd}
        role="slider"
        tabIndex={0}
        aria-valuemin={this.props.min}
        aria-valuemax={this.props.max}
        aria-valuenow={value}
        aria-orientation="horizontal"
        onKeyDown={this.onKeyDown}
        onFocus={this.props.changeActive}
        // FASE2-WIZARD-2b-bis: marca para que el clic en la pista sepa que el
        // puntero venía de un tope y no reposicione nada.
        data-slider-handle="true"
        {...ariaLabelMaybe}
      >
        <div
          className={classNames(css.visibleHandle, {
            [css.dragged]: this.state.dragging,
          })}
          style={visibleOffset}
        />
      </div>
    );
  }
}

export default Handle;
